import React, { useState, useMemo } from 'react';
import {
  Card,
  Text,
  Button,
  Badge,
  Caption1,
  Body1,
  Title3,
} from '@fluentui/react-components';
import {
  ArrowTrendingRegular,
  DocumentBulletListRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  AlertUrgentRegular,
} from '@fluentui/react-icons';
import { mockInvoices, mockSummary } from '../../data/mockInvoiceData';
import styles from './APInvoiceDashboard.module.css';

export interface APInvoiceDashboardProps {
  onInvoiceSelect?: (invoiceId: string) => void;
}

type FilterType = 'all' | 'high' | 'medium' | 'low' | 'duplicates';

const APInvoiceDashboard: React.FC<APInvoiceDashboardProps> = ({ onInvoiceSelect }) => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('high');

  // Filter invoices based on selected card
  const filteredInvoices = useMemo(() => {
    switch (selectedFilter) {
      case 'high':
        return mockInvoices.filter(inv => inv.priority_level === 'high');
      case 'medium':
        return mockInvoices.filter(inv => inv.priority_level === 'medium');
      case 'low':
        return mockInvoices.filter(inv => inv.priority_level === 'low');
      case 'duplicates':
        return mockInvoices.filter(inv => inv.duplicate_detected);
      default:
        return mockInvoices;
    }
  }, [selectedFilter]);

  // Mock recent activity data
  const recentActivity = [
    { id: 1, type: 'paid', vendor: 'Fabrikam Inc', amount: 12450, time: '2h ago', icon: <CheckmarkCircleRegular /> },
    { id: 2, type: 'approved', vendor: 'Contoso Ltd', amount: 8900, time: '3h ago', icon: <CheckmarkCircleRegular /> },
    { id: 3, type: 'flagged', vendor: 'Wide World Importers', amount: 15600, time: '5h ago', icon: <AlertUrgentRegular /> },
    { id: 4, type: 'rejected', vendor: 'Northwind Traders', amount: 6750, time: '1d ago', icon: <DismissCircleRegular /> },
    { id: 5, type: 'paid', vendor: 'Adventure Works', amount: 22100, time: '1d ago', icon: <CheckmarkCircleRegular /> },
  ];

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get activity type styling
  const getActivityStyle = (type: string) => {
    switch (type) {
      case 'paid':
        return { color: '#107C10', bg: '#DFF6DD' };
      case 'approved':
        return { color: '#0078D4', bg: '#DEECF9' };
      case 'flagged':
        return { color: '#F7630C', bg: '#FDE7E9' };
      case 'rejected':
        return { color: '#D13438', bg: '#FDE7E9' };
      default:
        return { color: '#605E5C', bg: '#F3F2F1' };
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      {/* Top Row - Summary Cards (Clickable) */}
      <div className={styles.summaryRow}>
        <Card 
          className={`${styles.summaryCard} ${selectedFilter === 'high' ? styles.summaryCardActive : ''}`}
          onClick={() => setSelectedFilter('high')}
        >
          <div className={styles.summaryIcon} style={{ color: '#D13438' }}>
            <AlertUrgentRegular style={{ fontSize: '24px' }} />
          </div>
          <div className={styles.summaryContent}>
            <Text className={styles.summaryValue}>{mockSummary.high}</Text>
            <Text className={styles.summaryLabel}>Aging Exceptions</Text>
            <Caption1 className={styles.summaryDetail}>Aging &gt; 30 days</Caption1>
            <Caption1 className={styles.summaryTrend} style={{ color: '#D13438' }}>
              ↑ +2 since yesterday
            </Caption1>
          </div>
        </Card>

        <Card 
          className={`${styles.summaryCard} ${selectedFilter === 'medium' ? styles.summaryCardActive : ''}`}
          onClick={() => setSelectedFilter('medium')}
        >
          <div className={styles.summaryIcon} style={{ color: '#F7630C' }}>
            <DocumentBulletListRegular style={{ fontSize: '24px' }} />
          </div>
          <div className={styles.summaryContent}>
            <Text className={styles.summaryValue}>{mockSummary.medium}</Text>
            <Text className={styles.summaryLabel}>Open Exceptions</Text>
            <Caption1 className={styles.summaryDetail}>Aging 8-30 days</Caption1>
            <Caption1 className={styles.summaryTrend} style={{ color: '#107C10' }}>
              ↓ -3 this week
            </Caption1>
          </div>
        </Card>

        <Card 
          className={`${styles.summaryCard} ${selectedFilter === 'low' ? styles.summaryCardActive : ''}`}
          onClick={() => setSelectedFilter('low')}
        >
          <div className={styles.summaryIcon} style={{ color: '#0078D4' }}>
            <CheckmarkCircleRegular style={{ fontSize: '24px' }} />
          </div>
          <div className={styles.summaryContent}>
            <Text className={styles.summaryValue}>{mockSummary.low}</Text>
            <Text className={styles.summaryLabel}>Resolved / Monitoring</Text>
            <Caption1 className={styles.summaryDetail}>Aging 0-7 days</Caption1>
            <Caption1 className={styles.summaryTrend} style={{ color: '#0078D4' }}>
              → Stable
            </Caption1>
          </div>
        </Card>

        <Card 
          className={`${styles.summaryCard} ${selectedFilter === 'duplicates' ? styles.summaryCardActive : ''}`}
          onClick={() => setSelectedFilter('duplicates')}
        >
          <div className={styles.summaryIcon} style={{ color: '#8764B8' }}>
            <DismissCircleRegular style={{ fontSize: '24px' }} />
          </div>
          <div className={styles.summaryContent}>
            <Text className={styles.summaryValue}>{mockSummary.duplicates}</Text>
            <Text className={styles.summaryLabel}>Duplicate / Policy Exceptions</Text>
            <Caption1 className={styles.summaryDetail}>Needs review</Caption1>
            <Caption1 className={styles.summaryTrend} style={{ color: '#8764B8' }}>
              Requires action
            </Caption1>
          </div>
        </Card>
      </div>

      {/* Bottom Row - 3 Columns */}
      <div className={styles.bottomRow}>
        {/* Left: Recent Activity */}
        <Card className={styles.activityCard}>
          <div className={styles.cardHeader}>
            <Title3>Recent Activity</Title3>
            <Caption1 className={styles.cardSubtitle}>Last 7 days</Caption1>
          </div>
          <div className={styles.activityList}>
            {recentActivity.map((activity) => {
              const style = getActivityStyle(activity.type);
              return (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityIcon} style={{ color: style.color, backgroundColor: style.bg }}>
                    {activity.icon}
                  </div>
                  <div className={styles.activityContent}>
                    <Body1 className={styles.activityVendor}>{activity.vendor}</Body1>
                    <Caption1 className={styles.activityDetails}>
                      {activity.type.toUpperCase()} • {formatCurrency(activity.amount)}
                    </Caption1>
                  </div>
                  <Caption1 className={styles.activityTime}>{activity.time}</Caption1>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Middle: Quick Actions */}
        <Card className={styles.actionsCard}>
          <div className={styles.cardHeader}>
            <Title3>Quick Actions</Title3>
          </div>
          <div className={styles.actionsList}>
            <Button 
              appearance="primary" 
              className={styles.actionButton}
              icon={<DocumentBulletListRegular />}
            >
              Review Exception Inbox
              <Caption1 className={styles.actionBadge}>4 pending</Caption1>
            </Button>
            <Button 
              appearance="outline" 
              className={styles.actionButton}
              icon={<ArrowTrendingRegular />}
            >
              Run Exception Analysis
              <Caption1 className={styles.actionBadge}>Last run: 2:15 AM</Caption1>
            </Button>
            <Button 
              appearance="outline" 
              className={styles.actionButton}
              icon={<CheckmarkCircleRegular />}
            >
              Approval Queue
              <Caption1 className={styles.actionBadge}>12 awaiting</Caption1>
            </Button>
          </div>
        </Card>

        {/* Right: Invoice List (filtered) */}
        <Card className={styles.invoiceListCard}>
          <div className={styles.cardHeader}>
            <Title3>
              {selectedFilter === 'high' && 'Exceptions Requiring Action'}
              {selectedFilter === 'medium' && 'Open Exceptions'}
              {selectedFilter === 'low' && 'Resolved / Monitoring'}
              {selectedFilter === 'duplicates' && 'Duplicate / Policy Exceptions'}
            </Title3>
            <Caption1 className={styles.cardSubtitle}>
              Showing {filteredInvoices.length} exception{filteredInvoices.length !== 1 ? 's' : ''}
            </Caption1>
          </div>
          <div className={styles.invoiceList}>
            {filteredInvoices.slice(0, 10).map((invoice) => (
              <div 
                key={invoice.invoice_id} 
                className={styles.invoiceItem}
                onClick={() => onInvoiceSelect?.(invoice.invoice_id)}
              >
                <div className={styles.invoiceHeader}>
                  <Body1 className={styles.invoiceId}>{invoice.invoice_id}</Body1>
                  <Badge 
                    appearance="filled" 
                    color={
                      invoice.priority_level === 'high' ? 'danger' :
                      invoice.priority_level === 'medium' ? 'warning' : 'success'
                    }
                    className={styles.priorityBadge}
                  >
                    {invoice.priority_score}
                  </Badge>
                </div>
                <Body1 className={styles.invoiceVendor}>{invoice.vendor_name}</Body1>
                <div className={styles.invoiceFooter}>
                  <Caption1 className={styles.invoiceAmount}>{formatCurrency(invoice.amount)}</Caption1>
                  <Caption1 className={styles.invoiceAging}>{invoice.aging_days}d aging</Caption1>
                  {invoice.duplicate_detected && (
                    <Badge appearance="filled" color="severe" size="small">DUP</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default APInvoiceDashboard;
